import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} [trigger]
 * @property {HTMLTemplateElement} [mediaTemplate]
 * @property {HTMLElement} [mediaMount]
 * @property {HTMLElement} mediaContainer
 * @property {HTMLDialogElement} [dialog]
 * @property {HTMLButtonElement} [closeButton]
 *
 * @extends {Component<Refs>}
 */
class BackedResearcherVideo extends Component {
  requiredRefs = ['mediaContainer'];
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {HTMLDialogElement | undefined} */
  #dialog;
  /** @type {HTMLElement | undefined} */
  #mount;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#dispose();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dispose();
  }

  #initialize() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.#dialog = this.refs.dialog;
    this.#mount = this.refs.mediaMount;
    this.#dialog?.addEventListener('close', () => {
      if (!this.#dialog?.open) this.#reset(true);
    }, { signal: this.#listeners.signal });
  }

  /** Creates the iframe only in response to opening the video. @param {MouseEvent} event */
  openVideo(event) {
    const { mediaTemplate, mediaMount, dialog } = this.refs;
    if (!(mediaTemplate instanceof HTMLTemplateElement) || !(mediaMount instanceof HTMLElement)) return;
    const iframe = mediaTemplate.content.firstElementChild?.cloneNode(true);
    if (!(iframe instanceof HTMLIFrameElement)) return;

    event.preventDefault();
    if (mediaMount.querySelector('iframe')) return;

    if (dialog instanceof HTMLDialogElement) {
      dialog.hidden = false;
      dialog.showModal();
      document.body.classList.add('backed-researcher-video-open');
    } else {
      this.refs.mediaContainer.classList.add('is-playing');
    }

    // Removing the iframe on close also stops playback and resets its position.
    mediaMount.appendChild(iframe);
    if (dialog) this.refs.closeButton?.focus();
    else iframe.focus();
  }

  /** Closes the video and stops even a player that is still loading. @param {MouseEvent} [event] */
  closeVideo(event) {
    if (this.#dialog?.open) this.#dialog.close();
    this.#reset(true);
  }

  /** @param {boolean} [restoreFocus] */
  #reset(restoreFocus = false) {
    this.#mount?.replaceChildren();
    if (this.#dialog) this.#dialog.hidden = true;
    this.refs.mediaContainer?.classList.remove('is-playing');
    document.body.classList.toggle(
      'backed-researcher-video-open',
      Boolean(document.querySelector('backed-researcher-video dialog[open]'))
    );
    if (restoreFocus && this.isConnected) this.refs.trigger?.focus();
  }

  #dispose() {
    this.#listeners?.abort();
    if (this.#dialog?.open) this.#dialog.close();
    this.#reset();
    this.#dialog = undefined;
    this.#mount = undefined;
  }
}

if (!customElements.get('backed-researcher-video')) {
  customElements.define('backed-researcher-video', BackedResearcherVideo);
}
